import { useState, useCallback } from 'preact/hooks';
import { usePaginatedList } from '../../hooks/use-paginated-list';
import { getVerdicts } from '../../api/verdicts';
import { Card } from '../../components/card';
import { Table, type Column } from '../../components/table';
import { Pagination } from '../../components/pagination';
import { Select } from '../../components/select';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { ConfidenceMeter } from '../../components/confidence-meter';
import type { Verdict } from '../../api/types';

const LIMIT = 20;

// These are the actual dietary/health frameworks Sift evaluates against
const FRAMEWORK_OPTIONS = [
  { value: '', label: 'All Frameworks' },
  { value: 'halal', label: 'Halal' },
  { value: 'allergen', label: 'Allergen' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'heart-healthy', label: 'Heart-Healthy' },
  { value: 'keto', label: 'Keto' },
  { value: 'renal', label: 'Renal' },
];

const EVALUATOR_OPTIONS = [
  { value: '', label: 'All Evaluators' },
  { value: 'rules', label: 'Rules' },
  { value: 'llm', label: 'LLM' },
];

const CONFIDENCE_OPTIONS = [
  { value: '', label: 'All Confidence' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function statusVariant(status: string | undefined): string {
  if (!status) return 'warn';
  const s = status.toLowerCase();
  if (s === 'halal' || s === 'permissible' || s === 'compliant' || s === 'safe') return 'ok';
  if (s === 'haram' || s === 'not_permissible' || s === 'non_compliant' || s === 'unsafe') return 'error';
  return 'warn';
}

export function VerdictsTab() {
  const [framework, setFramework] = useState('');
  const [evaluator, setEvaluator] = useState('');
  const [confidence, setConfidence] = useState('');

  const fetcher = useCallback(
    async (page: number) => {
      const res = await getVerdicts({
        page,
        limit: LIMIT,
        framework: framework || undefined,
        evaluator: evaluator || undefined,
        confidence: confidence || undefined,
      });
      return { items: res.verdicts, total: res.total };
    },
    [framework, evaluator, confidence],
  );

  const list = usePaginatedList<Verdict>(fetcher);

  const hasFilters = framework || evaluator || confidence;

  const clearFilters = () => {
    setFramework('');
    setEvaluator('');
    setConfidence('');
  };

  const columns: Column<Verdict>[] = [
    { header: 'Barcode', key: 'barcode', sortable: true },
    {
      header: 'Product',
      key: 'product_name',
      sortable: true,
      render: (r) => r.product_name ?? '\u2014',
    },
    {
      header: 'Framework',
      key: 'framework_id',
      sortable: true,
      render: (r) => (
        <Badge label={r.framework_id} variant="info" />
      ),
    },
    {
      header: 'Status',
      key: 'overall_status',
      render: (r) => (
        <Badge
          label={r.overall_status ?? 'unknown'}
          variant={statusVariant(r.overall_status)}
        />
      ),
    },
    {
      header: 'Confidence',
      key: 'confidence',
      render: (r) => r.confidence
        ? <ConfidenceMeter confidence={r.confidence} />
        : <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>{'\u2014'}</span>,
    },
    { header: 'Evaluator', key: 'evaluated_by' },
    { header: 'Date', key: 'evaluated_at' },
  ];

  return (
    <div>
      {/* Filters — applied immediately on change */}
      <Card>
        <h2>Filters</h2>
        <div class="filter-bar">
          <div class="filter-field">
            <label>Framework</label>
            <Select value={framework} onChange={setFramework} options={FRAMEWORK_OPTIONS} />
          </div>
          <div class="filter-field">
            <label>Evaluator</label>
            <Select value={evaluator} onChange={setEvaluator} options={EVALUATOR_OPTIONS} />
          </div>
          <div class="filter-field">
            <label>Confidence</label>
            <Select value={confidence} onChange={setConfidence} options={CONFIDENCE_OPTIONS} />
          </div>
          {hasFilters && (
            <div class="filter-actions">
              <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
            </div>
          )}
        </div>
      </Card>

      {/* Results */}
      <Card>
        <h2>
          Verdicts
          {list.total > 0 && (
            <span style={{ fontWeight: 400, fontSize: '0.72rem', marginLeft: 'var(--sp-2)', color: 'var(--text-tertiary)' }}>
              {list.total} total
            </span>
          )}
        </h2>
        {list.loading ? (
          <Spinner text="Loading verdicts" centered />
        ) : list.error ? (
          <Message text={list.error} type="error" />
        ) : list.items.length === 0 ? (
          <EmptyState icon="search" message="No verdicts found" />
        ) : (
          <>
            <Table<Record<string, unknown>>
              columns={columns as unknown as Column<Record<string, unknown>>[]}
              rows={list.items as unknown as Record<string, unknown>[]}
            />
            <Pagination
              page={list.page}
              total={list.total}
              limit={LIMIT}
              onPageChange={list.setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
